import React from 'react';
import { BookOpen, Star, Moon, Sun, Sparkles, Calendar, Clock, Heart, Brain, Globe, History, ArrowRight, ArrowUp } from 'lucide-react';
import AuthorBio, { ContentDisclaimer } from '../components/AuthorBio';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';

const ResourcesPage: React.FC = () => {
    // Set SEO meta tags for this page
    useSetMeta({
      title: 'Learning Resources - Astrology & Tarot Guides | ZodiAge',
      description: 'Browse our comprehensive learning center with guides on astrology, zodiac systems, tarot readings, and mystical traditions. Learn from trusted astrological resources.',
      ogImage: 'https://zodiage.com/logo.png'
    });
    return (
        <div className="space-y-12 animate-in fade-in duration-500 pb-12">
            {/* Header */}
            <article className="max-w-4xl mx-auto">
                <header className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                        Learning Center – Your Guide to Astrology, Zodiac & Tarot
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                        Explore comprehensive guides, tutorials, and educational resources to deepen your understanding 
                        of astrological systems, symbolic traditions, and the tools available on ZodiAge.
                    </p>
                </header>

                {/* Introduction */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mb-8">
                    <h2 className="text-xl font-bold text-white mb-3">Welcome to the ZodiAge Learning Center</h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-3">
                        Whether you're new to astrology or looking to expand your knowledge, this Learning Center provides 
                        structured educational content to help you understand the systems and symbols that have fascinated 
                        humanity for millennia. From the mathematical precision of age calculation to the rich symbolism of 
                        tarot, each topic is presented with clarity, context, and respect for both historical tradition and 
                        modern interpretation.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                        Our resources are designed to complement the interactive tools on ZodiAge, giving you the background 
                        knowledge to use them more meaningfully. All content is written for education and entertainment purposes, 
                        helping you engage with these symbolic systems as frameworks for self-reflection rather than predictive sciences.
                    </p>
                </section>
            </article>

            {/* Guide Categories */}
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
                
                {/* Age Calculation Guide */}
                <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 hover:border-primary/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-primary/20 p-3 rounded-xl">
                            <Clock size={24} className="text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Understanding Age Calculation</h3>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Learn how precise age calculation works, including the mathematics behind leap years, 
                        varying month lengths, and real-time counting. Understand why accurate age matters for 
                        legal documents, benefits, and personal milestones.
                    </p>
                    <div className="space-y-2 text-sm">
                        <h4 className="font-bold text-white">Key Topics:</h4>
                        <ul className="space-y-1 text-gray-400">
                            <li className="flex items-center gap-2"><span className="text-primary">•</span>Gregorian calendar mechanics</li>
                            <li className="flex items-center gap-2"><span className="text-primary">•</span>Leap year rules and exceptions</li>
                            <li className="flex items-center gap-2"><span className="text-primary">•</span>Western vs. East Asian age reckoning</li>
                            <li className="flex items-center gap-2"><span className="text-primary">•</span>Converting age to different units</li>
                        </ul>
                    </div>
                    <a href="/" className="inline-flex items-center gap-2 text-primary text-sm mt-4 hover:underline">
                        Try the Age Calculator <ArrowRight size={14} />
                    </a>
                </article>

                {/* Western Zodiac Guide */}
                <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 hover:border-yellow-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-yellow-400/20 p-3 rounded-xl">
                            <Sun size={24} className="text-yellow-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Western Astrology Basics</h3>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Explore the foundations of Western astrology, from the twelve zodiac signs to the concept 
                        of the "Big Three" (Sun, Moon, and Rising signs). Understand how birth charts are constructed 
                        and interpreted.
                    </p>
                    <div className="space-y-2 text-sm">
                        <h4 className="font-bold text-white">Key Topics:</h4>
                        <ul className="space-y-1 text-gray-400">
                            <li className="flex items-center gap-2"><span className="text-yellow-400">•</span>The 12 zodiac signs and their traits</li>
                            <li className="flex items-center gap-2"><span className="text-yellow-400">•</span>Elements: Fire, Earth, Air, Water</li>
                            <li className="flex items-center gap-2"><span className="text-yellow-400">•</span>Modalities: Cardinal, Fixed, Mutable</li>
                            <li className="flex items-center gap-2"><span className="text-yellow-400">•</span>Ruling planets and their meanings</li>
                        </ul>
                    </div>
                    <a href="/zodiac" className="inline-flex items-center gap-2 text-yellow-400 text-sm mt-4 hover:underline">
                        Explore Your Signs <ArrowRight size={14} />
                    </a>
                </article>

                {/* Chinese Zodiac Guide */}
                <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 hover:border-red-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-red-400/20 p-3 rounded-xl">
                            <Moon size={24} className="text-red-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Chinese Zodiac Traditions</h3>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Discover the 12-animal cycle of the Chinese zodiac, the Five Elements system, and how 
                        the lunar calendar determines your sign. Learn about cultural significance and compatibility patterns.
                    </p>
                    <div className="space-y-2 text-sm">
                        <h4 className="font-bold text-white">Key Topics:</h4>
                        <ul className="space-y-1 text-gray-400">
                            <li className="flex items-center gap-2"><span className="text-red-400">•</span>The 12 animal signs and their symbolism</li>
                            <li className="flex items-center gap-2"><span className="text-red-400">•</span>Five Elements: Wood, Fire, Earth, Metal, Water</li>
                            <li className="flex items-center gap-2"><span className="text-red-400">•</span>Lunar calendar vs. Gregorian calendar</li>
                            <li className="flex items-center gap-2"><span className="text-red-400">•</span>Yin/Yang polarity in the zodiac</li>
                        </ul>
                    </div>
                    <a href="/chinese-zodiage" className="inline-flex items-center gap-2 text-red-400 text-sm mt-4 hover:underline">
                        Find Your Animal <ArrowRight size={14} />
                    </a>
                </article>

                {/* Tarot Guide */}
                <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 hover:border-purple-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-400/20 p-3 rounded-xl">
                            <Sparkles size={24} className="text-purple-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Introduction to Tarot</h3>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Learn the structure of the tarot deck, the symbolism of Major and Minor Arcana, and how 
                        to interpret cards for personal reflection. Understand the Fool's Journey narrative arc.
                    </p>
                    <div className="space-y-2 text-sm">
                        <h4 className="font-bold text-white">Key Topics:</h4>
                        <ul className="space-y-1 text-gray-400">
                            <li className="flex items-center gap-2"><span className="text-purple-400">•</span>22 Major Arcana cards and their meanings</li>
                            <li className="flex items-center gap-2"><span className="text-purple-400">•</span>Four suits: Wands, Cups, Swords, Pentacles</li>
                            <li className="flex items-center gap-2"><span className="text-purple-400">•</span>Reversed card interpretations</li>
                            <li className="flex items-center gap-2"><span className="text-purple-400">•</span>Common spreads and layouts</li>
                        </ul>
                    </div>
                    <a href="/tarot" className="inline-flex items-center gap-2 text-purple-400 text-sm mt-4 hover:underline">
                        Draw a Card <ArrowRight size={14} />
                    </a>
                </article>

                {/* Life Progress Guide */}
                <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 hover:border-green-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-400/20 p-3 rounded-xl">
                            <Heart size={24} className="text-green-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Life Progress & Mindfulness</h3>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Explore the philosophical concept of "memento mori" and how visualizing life progress can 
                        inspire intentional living. Learn about life expectancy statistics and mortality awareness.
                    </p>
                    <div className="space-y-2 text-sm">
                        <h4 className="font-bold text-white">Key Topics:</h4>
                        <ul className="space-y-1 text-gray-400">
                            <li className="flex items-center gap-2"><span className="text-green-400">•</span>Understanding life expectancy statistics</li>
                            <li className="flex items-center gap-2"><span className="text-green-400">•</span>The purpose of mortality awareness</li>
                            <li className="flex items-center gap-2"><span className="text-green-400">•</span>Time visualization techniques</li>
                            <li className="flex items-center gap-2"><span className="text-green-400">•</span>Setting intentional priorities</li>
                        </ul>
                    </div>
                    <a href="/progress" className="inline-flex items-center gap-2 text-green-400 text-sm mt-4 hover:underline">
                        View Your Progress <ArrowRight size={14} />
                    </a>
                </article>

                {/* Life Milestones Guide */}
                <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 hover:border-cyan-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-cyan-400/20 p-3 rounded-xl">
                            <History size={24} className="text-cyan-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Astrological Life Cycles</h3>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Learn about significant astrological cycles and milestones, from the Saturn Return to the 
                        Chiron Return. Understand how these transits relate to common life experiences and transitions.
                    </p>
                    <div className="space-y-2 text-sm">
                        <h4 className="font-bold text-white">Key Topics:</h4>
                        <ul className="space-y-1 text-gray-400">
                            <li className="flex items-center gap-2"><span className="text-cyan-400">•</span>Saturn Return (ages 29-30, 58-60)</li>
                            <li className="flex items-center gap-2"><span className="text-cyan-400">•</span>Jupiter Return cycles (every 12 years)</li>
                            <li className="flex items-center gap-2"><span className="text-cyan-400">•</span>Uranus Opposition (mid-life, ~42)</li>
                            <li className="flex items-center gap-2"><span className="text-cyan-400">•</span>Chiron Return (age ~50)</li>
                        </ul>
                    </div>
                    <a href="/timeline" className="inline-flex items-center gap-2 text-cyan-400 text-sm mt-4 hover:underline">
                        Explore Your Timeline <ArrowRight size={14} />
                    </a>
                </article>
            </div>

            {/* In-Feed Ad for Resources Page */}
            <div className="max-w-4xl mx-auto px-4">
                <AdSpace type="in-feed" slot="6674482577" layoutKey="-fb+5w+4e-db+86" />
            </div>

            {/* Detailed Educational Sections */}
            <article className="max-w-4xl mx-auto space-y-8">
                
                {/* History of Astrology */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <div className="flex items-center gap-3 mb-4">
                        <Globe size={24} className="text-primary" />
                        <h2 className="text-2xl font-bold text-white">A Brief History of Astrology</h2>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        Astrology is one of humanity's oldest knowledge systems, with roots stretching back over 4,000 years to ancient 
                        Mesopotamia. The Babylonians developed early forms of celestial observation and omen interpretation, tracking 
                        planetary movements and their correlations with earthly events. This knowledge spread to ancient Egypt, Greece, 
                        Rome, and eventually around the world.
                    </p>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        In Hellenistic Greece (around 300 BCE), astrologers developed the system of twelve zodiac signs, planetary 
                        rulerships, and horoscopic interpretation that forms the foundation of Western astrology today. The twelve-sign 
                        zodiac was based on the constellation patterns along the ecliptic—the apparent path of the Sun across the sky.
                    </p>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        During the Renaissance, astrology was taught alongside astronomy in European universities. Notable figures 
                        including Johannes Kepler and Galileo practiced astrology while making their astronomical discoveries. The 
                        separation of astronomy (science) from astrology (symbolic system) is largely a modern development, emerging 
                        primarily in the 17th and 18th centuries with the Scientific Revolution.
                    </p>
                    <p className="text-gray-300 leading-relaxed">
                        Today, astrology exists as a cultural and psychological framework rather than a predictive science. Modern 
                        practitioners often view it as a symbolic language for self-reflection, understanding personality patterns, 
                        and exploring timing and cycles in life. Academic fields like archaeoastronomy and the history of science 
                        study astrology's rich historical and cultural significance.
                    </p>
                </section>

                {/* Understanding Symbolic Systems */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <div className="flex items-center gap-3 mb-4">
                        <Brain size={24} className="text-secondary" />
                        <h2 className="text-2xl font-bold text-white">Understanding Symbolic Systems</h2>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        Astrology, tarot, and similar practices are best understood as <strong className="text-white">symbolic systems</strong>—
                        frameworks that use images, archetypes, and patterns to organize experience and prompt reflection. Unlike empirical 
                        sciences that make testable predictions about physical reality, symbolic systems work through meaning, metaphor, 
                        and psychological resonance.
                    </p>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        The psychologist Carl Jung studied astrology and tarot as expressions of the "collective unconscious"—a shared 
                        layer of human psychology containing universal patterns (archetypes) that appear across cultures. From this 
                        perspective, zodiac signs and tarot cards represent aspects of human experience that everyone encounters: the 
                        initiating energy of Aries, the nurturing quality of Cancer, the transformation symbolized by the Death card.
                    </p>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        Modern users often engage with these systems through a practice called "active imagination"—using symbolic 
                        prompts to explore thoughts, feelings, and possibilities they might not otherwise consider. A tarot reading 
                        doesn't predict the future; it offers a symbolic framework for thinking about a situation from new angles.
                    </p>
                    <p className="text-gray-300 leading-relaxed">
                        This approach allows people to benefit from these ancient systems without requiring belief in supernatural 
                        causation. Whether you see astrology as cosmic influence or as a helpful psychological tool, the value lies 
                        in the quality of reflection and insight it promotes.
                    </p>
                </section>

                {/* The Four Elements */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <div className="flex items-center gap-3 mb-4">
                        <Star size={24} className="text-primary" />
                        <h2 className="text-2xl font-bold text-white">The Four Elements in Astrology</h2>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        The concept of four elements—Fire, Earth, Air, and Water—dates back to ancient Greek philosophy. In astrology, 
                        each zodiac sign belongs to one element, and understanding elements is key to understanding personality patterns 
                        and compatibility.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-red-900/20 p-4 rounded-xl border border-red-800/50">
                            <h4 className="font-bold text-red-400 mb-2">🔥 Fire Signs: Aries, Leo, Sagittarius</h4>
                            <p className="text-gray-300 text-sm">
                                Fire represents energy, passion, enthusiasm, and action. Fire signs are often described as courageous, 
                                spontaneous, and inspiring. They tend to be leaders who initiate rather than follow, though they may 
                                struggle with patience and follow-through.
                            </p>
                        </div>
                        <div className="bg-green-900/20 p-4 rounded-xl border border-green-800/50">
                            <h4 className="font-bold text-green-400 mb-2">🌍 Earth Signs: Taurus, Virgo, Capricorn</h4>
                            <p className="text-gray-300 text-sm">
                                Earth represents stability, practicality, material reality, and groundedness. Earth signs are often 
                                described as reliable, patient, and hardworking. They excel at building structures and achieving 
                                tangible results, though they may struggle with flexibility.
                            </p>
                        </div>
                        <div className="bg-yellow-900/20 p-4 rounded-xl border border-yellow-800/50">
                            <h4 className="font-bold text-yellow-400 mb-2">💨 Air Signs: Gemini, Libra, Aquarius</h4>
                            <p className="text-gray-300 text-sm">
                                Air represents intellect, communication, ideas, and social connection. Air signs are often described 
                                as curious, articulate, and objective. They excel at seeing multiple perspectives and connecting with 
                                others, though they may struggle with emotional depth.
                            </p>
                        </div>
                        <div className="bg-blue-900/20 p-4 rounded-xl border border-blue-800/50">
                            <h4 className="font-bold text-blue-400 mb-2">💧 Water Signs: Cancer, Scorpio, Pisces</h4>
                            <p className="text-gray-300 text-sm">
                                Water represents emotion, intuition, sensitivity, and depth. Water signs are often described as 
                                empathetic, perceptive, and imaginative. They excel at understanding feelings and hidden motivations, 
                                though they may struggle with boundaries and objectivity.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Practical Uses */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <div className="flex items-center gap-3 mb-4">
                        <BookOpen size={24} className="text-primary" />
                        <h2 className="text-2xl font-bold text-white">Practical Ways to Use ZodiAge</h2>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        Beyond entertainment, ZodiAge's tools can serve practical purposes in daily life. Here are some ways users 
                        apply these resources:
                    </p>
                    <div className="space-y-4">
                        <div className="bg-black/20 p-4 rounded-xl">
                            <h4 className="font-bold text-white mb-2">Document Preparation</h4>
                            <p className="text-gray-300 text-sm">
                                Use the age calculator to determine your exact age for legal documents, visa applications, insurance 
                                forms, or retirement benefit calculations. The precise day/month/year breakdown is useful when forms 
                                require specific formats.
                            </p>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <h4 className="font-bold text-white mb-2">Birthday Celebrations</h4>
                            <p className="text-gray-300 text-sm">
                                Track milestone birthdays (10,000 days old, 1 billion seconds, etc.) to create memorable celebration 
                                moments. The total days/hours/seconds view offers fun facts to share with friends and family.
                            </p>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <h4 className="font-bold text-white mb-2">Daily Reflection</h4>
                            <p className="text-gray-300 text-sm">
                                Use the daily tarot card or zodiac reading as a journaling prompt. Rather than taking the message 
                                literally, use it as a starting point to reflect on your day, challenges, or intentions.
                            </p>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <h4 className="font-bold text-white mb-2">Life Planning</h4>
                            <p className="text-gray-300 text-sm">
                                The life progress and timeline tools can prompt meaningful conversations about priorities, goals, 
                                and what matters most. They're particularly useful during major life transitions or when setting 
                                long-term intentions.
                            </p>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <h4 className="font-bold text-white mb-2">Cultural Learning</h4>
                            <p className="text-gray-300 text-sm">
                                The Chinese zodiac calculator and educational content offer insights into East Asian cultural 
                                traditions. This knowledge can be helpful for understanding cultural references, celebrating 
                                Lunar New Year, or connecting with friends from different backgrounds.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
                    <div className="space-y-4">
                        <div className="border-b border-cosmic-700/50 pb-4">
                            <h4 className="font-bold text-white mb-2">Is astrology scientifically proven?</h4>
                            <p className="text-gray-300 text-sm">
                                Astrology is not a science in the empirical sense. Scientific studies have not found evidence that 
                                celestial positions influence personality or events. However, astrology functions as a symbolic system—
                                like art or mythology—that can provide psychological value through reflection and meaning-making.
                            </p>
                        </div>
                        <div className="border-b border-cosmic-700/50 pb-4">
                            <h4 className="font-bold text-white mb-2">What's the difference between Western and Vedic astrology?</h4>
                            <p className="text-gray-300 text-sm">
                                Western astrology uses the tropical zodiac (based on seasons) while Vedic (Indian) astrology uses the 
                                sidereal zodiac (based on current constellation positions). Due to precession, there's about a 24-degree 
                                difference between them. ZodiAge uses the Western tropical system.
                            </p>
                        </div>
                        <div className="border-b border-cosmic-700/50 pb-4">
                            <h4 className="font-bold text-white mb-2">Why do I need my birth time for rising sign?</h4>
                            <p className="text-gray-300 text-sm">
                                The rising sign (Ascendant) is the zodiac sign on the eastern horizon at your exact moment of birth. 
                                Since the horizon changes every few minutes as Earth rotates, an accurate rising sign requires knowing 
                                your birth time to within about 15 minutes.
                            </p>
                        </div>
                        <div className="border-b border-cosmic-700/50 pb-4">
                            <h4 className="font-bold text-white mb-2">Can I use these tools for serious decision-making?</h4>
                            <p className="text-gray-300 text-sm">
                                ZodiAge is designed for entertainment and reflection. While the tools may inspire insights, important 
                                decisions about health, finances, relationships, or legal matters should always involve qualified 
                                professionals. Use these resources as prompts for thought, not as advice.
                            </p>
                        </div>
                        <div className="pb-4">
                            <h4 className="font-bold text-white mb-2">Is my data stored or shared?</h4>
                            <p className="text-gray-300 text-sm">
                                Your birth date and preferences are stored locally in your browser (localStorage). They are not uploaded 
                                to servers or shared with third parties. You can clear this data anytime through your browser settings. 
                                See our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> for details.
                            </p>
                        </div>
                    </div>
                </section>

                {/* More Resources */}
                <section className="bg-gradient-to-br from-primary/10 to-secondary/10 p-6 rounded-2xl border border-primary/30">
                    <h2 className="text-2xl font-bold text-white mb-4">Continue Your Journey</h2>
                    <p className="text-gray-300 leading-relaxed mb-4">
                        The Learning Center is just the beginning. Explore our other resources to deepen your knowledge:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <a href="/glossary" className="bg-black/20 p-4 rounded-xl hover:bg-black/30 transition-colors block">
                            <h4 className="font-bold text-white mb-1">Glossary</h4>
                            <p className="text-gray-400 text-sm">Look up any astrological or tarot term</p>
                        </a>
                        <a href="/about" className="bg-black/20 p-4 rounded-xl hover:bg-black/30 transition-colors block">
                            <h4 className="font-bold text-white mb-1">About ZodiAge</h4>
                            <p className="text-gray-400 text-sm">Learn about our mission and values</p>
                        </a>
                        <a href="/contact" className="bg-black/20 p-4 rounded-xl hover:bg-black/30 transition-colors block">
                            <h4 className="font-bold text-white mb-1">Contact Us</h4>
                            <p className="text-gray-400 text-sm">Questions or suggestions? Reach out</p>
                        </a>
                    </div>
                </section>

                {/* Multiplex Ad for Resources Page Footer */}
                <AdSpace type="multiplex" slot="9466105368" className="mt-8" />

                {/* Author Bio for E-E-A-T */}
                <AuthorBio variant="full" topic="astrology, tarot, and symbolic systems" />

                {/* Content Disclaimer */}
                <ContentDisclaimer variant="full" />
            </article>

            {/* Back to Top Button */}
            <div className="flex justify-center mt-8">
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105 hover:shadow-purple-600/50"
                >
                    <ArrowUp size={18} />
                    <span>Back to Top</span>
                </button>
            </div>
        </div>
    );
};

export default ResourcesPage;
